import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  EyeIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline';

import { useGetScholarshipsQuery, useGetUniversitiesQuery, useGetCountriesQuery, useGetCitiesQuery, useAddScholarshipMutation, useUpdateScholarshipMutation, useDeleteScholarshipMutation, useGetCurrenciesQuery, useGetEducationLevelsQuery } from '../store/api';
import { toast } from 'react-toastify';
import { Audio } from 'react-loader-spinner';
import TableHeader from '../components/TableHeader';
import ExportButton from '../components/ExportButton';
import { usePermission } from '../hooks/useAuth';

export default function Scholarships() {
  const [searchTerm, setSearchTerm] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [degreeFilter, setDegreeFilter] = useState('');
  const [coverageFilter, setCoverageFilter] = useState('');
  const [currencyFilter, setCurrencyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [editingScholarship, setEditingScholarship] = useState(null);
  const [viewingScholarship, setViewingScholarship] = useState(null);
  const [activeTab, setActiveTab] = useState('details');

  // Field adlarını Azərbaycan dilində göstərmək üçün helper function
  const getFieldDisplayName = (fieldName) => {
    const fieldNames = {
      'name': 'Təqaüd adı',
      'description': 'Təsvir',
      'university_id': 'Universitet',
      'country_id': 'Ölkə',
      'city_id': 'Şəhər',
      'degree_level': 'Dərəcə səviyyəsi',
      'field_of_study': 'Sahə',
      'amount': 'Məbləğ',
      'currency_id': 'Valyuta',
      'currency_code': 'Valyuta kodu',
      'amount_type': 'Ödəniş növü',
      'coverage_type': 'Əhatə növü',
      'application_deadline': 'Son müraciət tarixi',
      'requirements': 'Tələblər',
      'application_process': 'Müraciət prosesi',
      'contact_email': 'Email',
      'website': 'Vebsayt',
      'additional_benefits': 'Əlavə faydalar',
      'duration': 'Müddət',
      'is_active': 'Status'
    };
    
    return fieldNames[fieldName] || fieldName;
  };
  
  // Permission hooks - strictly permission-based
  const canAddScholarship = usePermission('scholarship.add');
  const canEditScholarship = usePermission('scholarship.edit');
  const canDeleteScholarship = usePermission('scholarship.delete');
  const canViewScholarship = usePermission('scholarship.view'); // Already being used in route protection
  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalScholarshipCount, setTotalScholarshipCount] = useState(0);
  const [scholarshipsData, setScholarshipsData] = useState(null);
  const [isExport, setIsExport] = useState(0);
  const [exportData, setExportData] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    university_id: '',
    country_id: '',
    city_id: '',
    degree_level: [],
    field_of_study: '',
    amount: '',
    currency_id: '',
    amount_type: '',
    coverage_type: '',
    application_deadline: '',
    requirements: '',
    application_process: '',
    contact_email: '',
    website: '',
    additional_benefits: '',
    duration: '',
    is_active: 1
  });

  // Get Scholarships Query
  const { data, isError, error, isSuccess, isLoading } = useGetScholarshipsQuery({
    keyword: searchTerm,
    country_id: countryFilter,
    degree_level: degreeFilter,
    coverage_type: coverageFilter,
    currency: currencyFilter,
    is_active: statusFilter,
    is_export: isExport,
    page: currentPage,
    limit: itemsPerPage
  });

  // Get Universities for filter
  const { data: universitiesData } = useGetUniversitiesQuery({
    keyword: '',
    is_active: '1',
    is_export: 0,
    page: 1,
    limit: 1000
  });

  // Get Countries for filter
  const { data: countriesData } = useGetCountriesQuery({
    keyword: '',
    is_active: '1',
    is_export: 0,
    page: 1,
    limit: 1000
  });

  // Get Cities for filter
  const { data: citiesData } = useGetCitiesQuery({
    keyword: '',
    is_active: '1',
    is_export: 0,
    page: 1,
    limit: 1000
  });

  // Get Currencies for form
  const { data: currenciesData } = useGetCurrenciesQuery();

  // Get Education Levels for form
  const { data: educationLevelsData } = useGetEducationLevelsQuery();

  // Extract actual data arrays from API responses
  const currencies = currenciesData?.data || currenciesData || [];
  const educationLevels = educationLevelsData?.data || educationLevelsData || [];

  // Helper function to get degree level names from IDs
  const getDegreeLevelNames = (degreeLevelIds) => {
    if (!degreeLevelIds || !educationLevels) return '';
    
    const ids = typeof degreeLevelIds === 'string' ? 
      degreeLevelIds.split(',').map(id => id.trim()) : 
      degreeLevelIds;
    
    const names = ids.map(id => {
      const level = educationLevels.find(l => l.id && l.id.toString() === id.toString());
      return level ? level.name : id;
    }).filter(Boolean);
    
    return names.join(', ');
  };

  useEffect(() => {
    if (isExport == 1) {
      const transformedData = data.data.map(scholarship => ({
        'Təqaüd Adı': scholarship.name,
        'Universitet': scholarship.university?.name || scholarship.university || '',
        'Ölkə': scholarship.country?.name || scholarship.country || '',
        'Şəhər': scholarship.city?.name || scholarship.city || '',
        'Dərəcə': getDegreeLevelNames(scholarship.degree_level),
        'Sahə': scholarship.field_of_study,
        'Məbləğ': scholarship.amount,
        'Valyuta': typeof scholarship.currency === 'object' ? 
          (scholarship.currency?.code || scholarship.currency?.name || '') : 
          (scholarship.currency || ''),
        'Növ': scholarship.amount_type,
        'Əhatə': scholarship.coverage_type,
        'Son Tarix': scholarship.application_deadline,
        'Tələblər': scholarship.requirements || '',
        'Əlaqə': scholarship.contact_email,
        'Veb Sayt': scholarship.website || '',
        'Status': scholarship.is_active ? 'Aktiv' : 'Deaktiv',
        'ID': scholarship.id
      }));

      setExportData(transformedData);
    } else {
      if (!isLoading && data) {
        setScholarshipsData(data.data.data);
        setTotalPages(Math.ceil(data.data.total / data.data.per_page));
        setTotalScholarshipCount(data.data.total);
      }
    }
  }, [data, isSuccess, isError, isLoading, error]);

  // Add Scholarship Mutation
  const [addScholarship, {
    data: createResponse,
    isError: createIsError,
    error: createError,
    isSuccess: createIsSuccess,
    isLoading: createIsLoading
  }] = useAddScholarshipMutation();

  useEffect(() => {
    if (!createIsLoading && createResponse && createIsSuccess) {
      toast.success(createResponse.message);
      setIsModalOpen(false);
      setFormData({
        name: '',
        description: '',
        university_id: '',
        country_id: '',
        city_id: '',
        degree_level: [],
        field_of_study: '',
        amount: '',
        currency_id: '',
        amount_type: '',
        coverage_type: '',
        application_deadline: '',
        requirements: '',
        application_process: '',
        contact_email: '',
        website: '',
        additional_benefits: '',
        duration: '',
        is_active: 1
      });
    }

    if (createIsError && createError) {
      // Backend'dən gələn error response'u düzgün handle etmək
      const errorData = createError?.data;
      
      if (errorData && errorData.errors) {
        // Validation errorları - hər field error'u ayrı toast olaraq göstər
        Object.keys(errorData.errors).forEach(field => {
          const fieldErrors = errorData.errors[field];
          if (Array.isArray(fieldErrors)) {
            fieldErrors.forEach(errorMessage => {
              toast.error(`${getFieldDisplayName(field)}: ${errorMessage}`);
            });
          }
        });
      } else if (errorData && errorData.message) {
        // General error message
        toast.error(errorData.message);
      } else {
        // Fallback error message
        toast.error('Təqaüd proqramı əlavə edilərkən xəta baş verdi');
      }
    }
  }, [createResponse, createIsSuccess, createIsError, createIsLoading, createError]);

  // Update Scholarship Mutation
  const [updateScholarship, {
    data: updateData,
    isError: updateIsError,
    error: updateError,
    isSuccess: updateIsSuccess,
    isLoading: updateIsLoading
  }] = useUpdateScholarshipMutation();

  useEffect(() => {
    if (!updateIsLoading && updateData && updateIsSuccess) {
      toast.success(updateData.message);
      setIsModalOpen(false);
      setEditingScholarship(null);
      setFormData({
        name: '',
        description: '',
        university_id: '',
        country_id: '',
        city_id: '',
        degree_level: [],
        field_of_study: '',
        amount: '',
        currency_id: '',
        amount_type: '',
        coverage_type: '',
        application_deadline: '',
        requirements: '',
        application_process: '',
        contact_email: '',
        website: '',
        additional_benefits: '',
        duration: '',
        is_active: 1
      });
    }

    if (updateIsError && updateError) {
      // Backend'dən gələn error response'u düzgün handle etmək
      const errorData = updateError?.data;
      
      if (errorData && errorData.errors) {
        // Validation errorları - hər field error'u ayrı toast olaraq göstər
        Object.keys(errorData.errors).forEach(field => {
          const fieldErrors = errorData.errors[field];
          if (Array.isArray(fieldErrors)) {
            fieldErrors.forEach(errorMessage => {
              toast.error(`${getFieldDisplayName(field)}: ${errorMessage}`);
            });
          }
        });
      } else if (errorData && errorData.message) {
        // General error message
        toast.error(errorData.message);
      } else {
        // Fallback error message
        toast.error('Təqaüd proqramı yenilənərkən xəta baş verdi');
      }
    }
  }, [updateData, updateIsSuccess, updateIsError, updateIsLoading, updateError]);

  // Delete Scholarship Mutation
  const [deleteScholarship, {
    data: deleteRes,
    isError: deleteIsError,
    error: deleteError,
    isSuccess: deleteIsSuccess,
    isLoading: deleteIsLoading
  }] = useDeleteScholarshipMutation();

  useEffect(() => {
    if (!deleteIsLoading && deleteRes && deleteIsSuccess) {
      toast.success(deleteRes.message);
    }
    
    if (deleteIsError && deleteError) {
      // Backend'dən gələn error response'u düzgün handle etmək
      const errorData = deleteError?.data;
      
      if (errorData && errorData.message) {
        toast.error(errorData.message);
      } else {
        toast.error("Təqaüd proqramı silinərkən xəta baş verdi");
      }
    }
  }, [deleteIsSuccess, deleteIsError, deleteRes, deleteError, deleteIsLoading]);

  // Pagination calculations
  const startIndex = (currentPage - 1) * itemsPerPage;

  const exportHeaders = [
    'Təqaüd Adı', 'Universitet', 'Ölkə', 'Şəhər', 'Dərəcə', 'Sahə', 'Məbləğ', 
    'Valyuta', 'Növ', 'Əhatə', 'Son Tarix', 'Tələblər', 'Əlaqə', 'Veb Sayt', 'Status', 'ID'
  ];

  const handleAdd = () => {
    setEditingScholarship(null);
    setFormData({
      name: '',
      description: '',
      university_id: '',
      country_id: '',
      city_id: '',
      degree_level: [],
      field_of_study: '',
      amount: '',
      currency_id: '',
      amount_type: '',
      coverage_type: '',
      application_deadline: '',
      requirements: '',
      application_process: '',
      contact_email: '',
      website: '',
      additional_benefits: '',
      duration: '',
      is_active: 1
    });
    setIsModalOpen(true);
  };

  const handleEdit = (scholarship) => {
    setEditingScholarship(scholarship);
    setFormData({
      name: scholarship.name,
      description: scholarship.description,
      university_id: scholarship.university_id?.toString() || '',
      country_id: scholarship.country_id?.toString() || '',
      city_id: scholarship.city_id?.toString() || '',
      degree_level: scholarship.degree_level ? scholarship.degree_level.split(',').map(id => id.trim()) : [],
      field_of_study: scholarship.field_of_study,
      amount: scholarship.amount,
      currency_id: scholarship.currency_id ? scholarship.currency_id.toString() : 
        (typeof scholarship.currency === 'object' ? scholarship.currency?.id?.toString() || '' : ''),
      amount_type: scholarship.amount_type,
      coverage_type: scholarship.coverage_type,
      application_deadline: scholarship.application_deadline ? 
        (scholarship.application_deadline.includes('T') 
          ? scholarship.application_deadline.split('T')[0] 
          : scholarship.application_deadline.match(/^\d{4}-\d{2}-\d{2}$/) 
            ? scholarship.application_deadline 
            : '') 
        : '',
      requirements: scholarship.requirements,
      application_process: scholarship.application_process,
      contact_email: scholarship.contact_email,
      website: scholarship.website,
      additional_benefits: scholarship.additional_benefits,
      duration: scholarship.duration,
      is_active: scholarship.is_active
    });
    setIsModalOpen(true);
  };

  const handleView = (scholarship) => {
    setViewingScholarship(scholarship);
    setActiveTab('details');
    setIsViewModalOpen(true);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setCountryFilter('');
    setDegreeFilter('');
    setCoverageFilter('');
    setCurrencyFilter('');
    setStatusFilter('');
    setCurrentPage(1);
  };

  const handleDelete = (scholarshipId, scholarshipName) => {
    if (window.confirm(`"${scholarshipName}" təqaüd proqramını silmək istədiyinizə əminsiniz?`)) {
      deleteScholarship(scholarshipId);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Prepare data for submission
    const submissionData = {
      ...formData,
      degree_level: Array.isArray(formData.degree_level) 
        ? formData.degree_level.join(',') 
        : formData.degree_level
    };
    
    if (editingScholarship) {
      updateScholarship({ id: editingScholarship?.id, obj: submissionData });
    } else {
      addScholarship(submissionData);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Get scholarship applications for the viewing scholarship
  const getScholarshipApplications = (scholarshipId) => {
    // TODO: Replace with API call to get scholarship applications
    return [];
  };

  // Get unique values for filters from scholarships data
  const uniqueCountries = countriesData?.data?.data || [];
  const uniqueDegrees = scholarshipsData ? [...new Set(scholarshipsData.flatMap(s => s.degree_level?.split(', ') || []))] : [];
  const uniqueCoverageTypes = scholarshipsData ? [...new Set(scholarshipsData.map(s => s.coverage_type))] : [];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Təqaüd Proqramları</h1>
          <p className="mt-1 text-sm text-gray-600">
            Beynəlxalq təqaüd imkanlarını idarə edin
          </p>
          <div className="mt-2 flex items-center space-x-4">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200">
              Ümumi: {totalScholarshipCount}
            </span>
            {(searchTerm || countryFilter || degreeFilter || coverageFilter || currencyFilter || statusFilter) && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200">
                Tapılan: {totalScholarshipCount}
              </span>
            )}
          </div>
        </div>
        <div className="mt-4 sm:mt-0 sm:flex sm:space-x-3">
          <ExportButton
            setIsExport={setIsExport}
            data={exportData}
            filename="təqaüdlər"
            headers={exportHeaders}
          />
          {canAddScholarship && (
            <button
              onClick={handleAdd}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Yeni Təqaüd
            </button>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="space-y-4">
        {/* First Row - Search and Main Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Ad, təsvir, universitet ilə axtarış..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div>
            <Select
              value={countryFilter ? {
                value: countryFilter,
                label: uniqueCountries.find(country => country.id.toString() === countryFilter)?.name || countryFilter
              } : null}
              onChange={(selectedOption) => setCountryFilter(selectedOption?.value || '')}
              options={uniqueCountries.map(country => ({
                value: country.id.toString(),
                label: country.name
              }))}
              placeholder="Bütün ölkələr"
              isSearchable={true}
              isClearable={true}
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                  boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
                  minHeight: '42px',
                  '&:hover': {
                    borderColor: state.isFocused ? '#3b82f6' : '#9ca3af'
                  }
                }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isSelected 
                    ? '#3b82f6' 
                    : state.isFocused 
                    ? '#dbeafe' 
                    : 'white',
                  color: state.isSelected ? 'white' : '#374151'
                })
              }}
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>
          <div>
            <Select
              value={statusFilter ? {
                value: statusFilter,
                label: statusFilter === 'active' ? 'Yalnız aktiv' : 'Yalnız deaktiv'
              } : null}
              onChange={(selectedOption) => setStatusFilter(selectedOption?.value || '')}
              options={[
                { value: '1', label: 'Yalnız aktiv' },
                { value: '0', label: 'Yalnız deaktiv' }
              ]}
              placeholder="Bütün statuslar"
              isSearchable={true}
              isClearable={true}
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                  boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
                  minHeight: '42px',
                  '&:hover': {
                    borderColor: state.isFocused ? '#3b82f6' : '#9ca3af'
                  }
                }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isSelected 
                    ? '#3b82f6' 
                    : state.isFocused 
                    ? '#dbeafe' 
                    : 'white',
                  color: state.isSelected ? 'white' : '#374151'
                })
              }}
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>
          
          {/* Hidden filters - uncomment if needed */}
          {/* 
          <div>
            <Select
              value={degreeFilter ? {
                value: degreeFilter,
                label: degreeFilter
              } : null}
              onChange={(selectedOption) => setDegreeFilter(selectedOption?.value || '')}
              options={uniqueDegrees.map(degree => ({
                value: degree,
                label: degree
              }))}
              placeholder="Bütün dərəcələr"
              isSearchable={true}
              isClearable={true}
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                  boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
                  minHeight: '42px',
                  '&:hover': {
                    borderColor: state.isFocused ? '#3b82f6' : '#9ca3af'
                  }
                }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isSelected 
                    ? '#3b82f6' 
                    : state.isFocused 
                    ? '#dbeafe' 
                    : 'white',
                  color: state.isSelected ? 'white' : '#374151'
                })
              }}
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>
          */}
          
          {/* 
          <div>
            <Select
              value={coverageFilter ? {
                value: coverageFilter,
                label: coverageFilter
              } : null}
              onChange={(selectedOption) => setCoverageFilter(selectedOption?.value || '')}
              options={uniqueCoverageTypes.map(coverage => ({
                value: coverage,
                label: coverage
              }))}
              placeholder="Bütün əhatə növləri"
              isSearchable={true}
              isClearable={true}
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                  boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
                  minHeight: '42px',
                  '&:hover': {
                    borderColor: state.isFocused ? '#3b82f6' : '#9ca3af'
                  }
                }),
                option: (provided, state) => ({
                  ...provided,
                  backgroundColor: state.isSelected 
                    ? '#3b82f6' 
                    : state.isFocused 
                    ? '#dbeafe' 
                    : 'white',
                  color: state.isSelected ? 'white' : '#374151'
                })
              }}
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>
          */}
        </div>

        {/* Second Row - Clear Button */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-1">
            <button
              onClick={handleClearFilters}
              disabled={!searchTerm && !countryFilter && !statusFilter}
              className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed h-[42px] flex items-center justify-center"
            >
              Filtri təmizlə
            </button>
          </div>
          <div className="md:col-span-3">
            {/* Empty space for better alignment */}
          </div>
        </div>
      </div>

      <TableHeader
        currentPage={currentPage}
        setItemsPerPage={setItemsPerPage}
        itemsPerPage={itemsPerPage}
        setCurrentPage={setCurrentPage}
        startIndex={startIndex}
        title={"Təqaüd Proqramları"}
        totalPages={totalPages}
        totalRecordCount={totalScholarshipCount}
      />

      {/* Scholarships table */}
      <div className="bg-white shadow-lg overflow-hidden sm:rounded-xl border border-blue-100">
        <div className="px-6 py-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-blue-50 to-blue-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    <div className="flex items-center space-x-2">
                      <span>Təqaüd Proqramı</span>
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Ölkə/Universitet
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Dərəcə
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Məbləğ
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Əhatə
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Son Tarix
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Status
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Əməliyyatlar
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {!isLoading && scholarshipsData?.length > 0 && scholarshipsData.map((scholarship) => (
                  <tr key={scholarship.id} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100 transition-all duration-200 group">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`flex-shrink-0 h-3 w-3 rounded-full mr-3 transition-colors ${
                          scholarship.is_active ? 'bg-blue-600 group-hover:bg-blue-700' : 'bg-gray-400 group-hover:bg-gray-500'
                        }`}></div>
                        <div>
                          <div className={`text-sm font-semibold transition-colors ${
                            scholarship.is_active ? 'text-gray-900 group-hover:text-blue-900' : 'text-gray-500 group-hover:text-gray-600'
                          }`}>
                            {scholarship.name}
                          </div>
                          <div className="text-xs text-gray-500 max-w-48 truncate" title={scholarship.description}>
                            {scholarship.description}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 font-medium">
                        {scholarship.country?.name || scholarship.country || 'N/A'}
                      </div>
                      <div className="text-xs text-gray-500 truncate max-w-32" title={scholarship.university?.name || scholarship.university || ''}>
                        {scholarship.university?.name || scholarship.university || 'N/A'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                        {(() => {
                          const degreeNames = getDegreeLevelNames(scholarship.degree_level);
                          const firstDegree = degreeNames.split(', ')[0];
                          const hasMultiple = degreeNames.includes(', ');
                          return `${firstDegree}${hasMultiple ? ' +' : ''}`;
                        })()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="bg-blue-50 px-3 py-1 rounded-md border border-blue-100">
                        <div className="text-sm font-semibold text-gray-900">
                          {scholarship.amount} {typeof scholarship.currency === 'object' ? 
                            (scholarship.currency?.code || scholarship.currency?.name || '') : 
                            (scholarship.currency || '')}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">{scholarship.amount_type}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        scholarship.coverage_type === 'Tam təqaüd' 
                          ? 'bg-green-100 text-green-800 border border-green-200' 
                          : 'bg-blue-100 text-blue-800 border border-blue-200'
                      }`}>
                        {scholarship.coverage_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{scholarship.application_deadline ? new Date(scholarship.application_deadline).toLocaleDateString('az-AZ') : 'Tarix yoxdur'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        scholarship.is_active 
                          ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' 
                          : 'bg-red-100 text-red-800 border border-red-200'
                      }`}>
                        {scholarship.is_active ? 'Aktiv' : 'Deaktiv'}
                      </span>
                    </td>
                                          <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex justify-end space-x-2">
                          {canViewScholarship && (
                            <button
                              onClick={() => handleView(scholarship)}
                              className="inline-flex items-center p-2 bg-green-100 text-green-600 hover:bg-green-200 hover:text-green-800 rounded-lg transition-all duration-200 hover:scale-105"
                              title="Ətraflı bax"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                          )}
                          {canEditScholarship && (
                            <button
                              onClick={() => handleEdit(scholarship)}
                              className="inline-flex items-center p-2 bg-blue-100 text-blue-600 hover:bg-blue-200 hover:text-blue-800 rounded-lg transition-all duration-200 hover:scale-105"
                              title="Redaktə et"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          )}
                          {canDeleteScholarship && (
                            <button
                              onClick={() => handleDelete(scholarship.id, scholarship.name)}
                              className="inline-flex items-center p-2 bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-800 rounded-lg transition-all duration-200 hover:scale-105"
                              title="Sil"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!isLoading && scholarshipsData?.length === 0 && (
            <div className="text-center py-16">
              <div className="mx-auto h-16 w-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mb-4">
                <svg className="h-8 w-8 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Təqaüd tapılmadı</h3>
              <p className="text-sm text-gray-500 max-w-sm mx-auto">
                Axtarış şərtlərinizi dəyişin və ya yeni təqaüd əlavə edin.
              </p>
              {canAddScholarship && (
                <button
                  onClick={handleAdd}
                  className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  İlk Təqaüdü Əlavə Et
                </button>
              )}
            </div>
          )}

          {isLoading && (
            <div className="flex justify-center items-center py-16">
              <Audio
                height="80"
                width="80"
                radius="9"
                color="blue"
                ariaLabel="loading"
                wrapperStyle={{}}
                wrapperClass=""
              />
            </div>
          )}
        </div>
      </div>

      {/* View Modal - Will be added in the next section */}
      {isViewModalOpen && viewingScholarship && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setIsViewModalOpen(false)} />
            
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full sm:p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className={`h-3 w-3 rounded-full ${viewingScholarship.is_active ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {viewingScholarship.name} - Ətraflı Məlumat
                  </h3>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                    {viewingScholarship.coverage_type}
                  </span>
                </div>
                <button
                  onClick={() => setIsViewModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="text-2xl">&times;</span>
                </button>
              </div>

              {/* Tabs */}
              <div className="border-b border-gray-200 mb-6">
                <nav className="-mb-px flex space-x-8">
                  <button
                    onClick={() => setActiveTab('details')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'details'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Təqaüd Təfərrüatları
                  </button>
                  <button
                    onClick={() => setActiveTab('applications')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === 'applications'
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Müraciətlər ({getScholarshipApplications(viewingScholarship.id).length})
                  </button>
                </nav>
              </div>

              {/* Tab Content */}
              <div className="min-h-64">
                {activeTab === 'details' && (
                  <div className="space-y-6">
                    {/* Scholarship Information Card */}
                    <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="h-8 w-8 bg-blue-600 rounded-lg flex items-center justify-center">
                          <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                          </svg>
                        </div>
                        <h4 className="text-lg font-semibold text-blue-900">Təqaüd Məlumatları</h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Təqaüd Adı</div>
                          <div className="text-sm font-semibold text-gray-900">{viewingScholarship.name}</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Dərəcə</div>
                          <div className="text-sm font-semibold text-gray-900">{getDegreeLevelNames(viewingScholarship.degree_level)}</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Sahə</div>
                          <div className="text-sm font-semibold text-gray-900">{viewingScholarship.field_of_study}</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Məbləğ</div>
                          <div className="text-sm font-semibold text-gray-900">
                            {viewingScholarship.amount} {typeof viewingScholarship.currency === 'object' ? 
                              (viewingScholarship.currency?.code || viewingScholarship.currency?.name || '') : 
                              (viewingScholarship.currency || '')}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">{viewingScholarship.amount_type}</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Əhatə Növü</div>
                          <div className="text-sm font-semibold text-gray-900">{viewingScholarship.coverage_type}</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Müddət</div>
                          <div className="text-sm font-semibold text-gray-900">{viewingScholarship.duration}</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Son Müraciət</div>
                          <div className="text-sm font-semibold text-gray-900">{viewingScholarship.application_deadline ? new Date(viewingScholarship.application_deadline).toLocaleDateString('az-AZ') : 'Tarix yoxdur'}</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Status</div>
                          <div className={`text-sm font-semibold ${viewingScholarship.is_active ? 'text-green-600' : 'text-red-600'}`}>
                            {viewingScholarship.is_active ? 'Aktiv' : 'Deaktiv'}
                          </div>
                        </div>
                      </div>
                      <div className="mt-4">
                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-2">Təqaüd Təsviri</div>
                          <div className="text-sm text-gray-900">{viewingScholarship.description}</div>
                        </div>
                      </div>
                    </div>

                    {/* University Information Card */}
                    <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="h-8 w-8 bg-blue-600 rounded-lg flex items-center justify-center">
                          <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                        <h4 className="text-lg font-semibold text-blue-900">Universitet və Yer Məlumatları</h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Universitet</div>
                          <div className="text-sm font-semibold text-gray-900">{viewingScholarship.university?.name || viewingScholarship.university || 'N/A'}</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Ölkə</div>
                          <div className="text-sm font-semibold text-gray-900">{viewingScholarship.country?.name || viewingScholarship.country || 'N/A'}</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Şəhər</div>
                          <div className="text-sm font-semibold text-gray-900">{viewingScholarship.city?.name || viewingScholarship.city || 'N/A'}</div>
                        </div>
                      </div>
                    </div>

                    {/* Application Information Card */}
                    <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="h-8 w-8 bg-blue-600 rounded-lg flex items-center justify-center">
                          <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <h4 className="text-lg font-semibold text-blue-900">Müraciət Məlumatları</h4>
                      </div>
                      <div className="space-y-4">
                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-2">Tələb Olunan Sənədlər</div>
                          <div className="text-sm text-gray-900">{viewingScholarship.requirements}</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-2">Müraciət Prosesi</div>
                          <div className="text-sm text-gray-900">{viewingScholarship.application_process}</div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-2">Əlavə Faydalar</div>
                          <div className="text-sm text-gray-900">{viewingScholarship.additional_benefits}</div>
                        </div>
                      </div>
                    </div>

                    {/* Contact Information Card */}
                    <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="h-8 w-8 bg-blue-600 rounded-lg flex items-center justify-center">
                          <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <h4 className="text-lg font-semibold text-blue-900">Əlaqə Məlumatları</h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Email</div>
                          <div className="text-sm text-gray-900">
                            <a href={`mailto:${viewingScholarship.contact_email}`} className="text-blue-600 hover:underline">
                              {viewingScholarship.contact_email}
                            </a>
                          </div>
                        </div>
                        <div className="bg-white p-4 rounded-lg border border-blue-100">
                          <div className="text-xs font-medium text-blue-600 uppercase tracking-wide mb-1">Vebsayt</div>
                          <div className="text-sm text-gray-900">
                            <a href={viewingScholarship.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center">
                              <GlobeAltIcon className="h-4 w-4 mr-1" />
                              {viewingScholarship.website?.replace('https://', '')}
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'applications' && (
                  <div className="space-y-3">
                    {getScholarshipApplications(viewingScholarship.id).length > 0 ? (
                      getScholarshipApplications(viewingScholarship.id).map((student) => (
                        <div key={student.id} className="bg-white p-4 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="h-10 w-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                                <span className="text-xs font-bold text-white">
                                  {student.firstName[0]}{student.lastName[0]}
                                </span>
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center space-x-2">
                                  <div className="font-medium text-gray-900">
                                    {student.firstName} {student.lastName}
                                  </div>
                                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                    student.statusId === 6 ? 'bg-green-100 text-green-800 border border-green-200' :
                                    student.statusId === 4 ? 'bg-blue-100 text-blue-800 border border-blue-200' :
                                    student.statusId === 3 ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
                                    'bg-gray-100 text-gray-800 border border-gray-200'
                                  }`}>
                                    {student.status}
                                  </span>
                                </div>
                                <div className="flex items-center space-x-4 text-xs text-gray-500 mt-1">
                                  <span>📞 {student.phone}</span>
                                  <span>🎓 {student.specialization}</span>
                                  <span>📊 GPA: {student.gpa}</span>
                                  <span>🌍 {student.languageExam}</span>
                                </div>
                                <div className="text-xs text-gray-600 mt-1">
                                  <strong>Konsultant:</strong> {student.consultant} | 
                                  <strong> Müraciət tarixi:</strong> {student.firstContactDate} |
                                  <strong> Mənbə:</strong> {student.source}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-xs text-gray-500">
                                Maliyyə proqnozu
                              </div>
                              <div className="text-sm font-medium text-gray-900">
                                {student.financialForecast}
                              </div>
                              <div className="text-xs text-blue-600 mt-1">
                                {student.tuitionFeeRange}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8">
                        <div className="mx-auto h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                          <svg className="h-6 w-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                          </svg>
                        </div>
                        <h5 className="text-lg font-medium text-gray-900 mb-2">Müraciət tapılmadı</h5>
                        <p className="text-gray-500">Bu təqaüd proqramına hələ heç bir müraciət yoxdur</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="mt-6 flex justify-end space-x-3">
                {canEditScholarship && (
                  <button
                    onClick={() => {
                      setIsViewModalOpen(false);
                      handleEdit(viewingScholarship);
                    }}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <PencilIcon className="-ml-1 mr-2 h-4 w-4" />
                    Redaktə Et
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal - Will be added in the next section */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={() => setIsModalOpen(false)} />
            
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
              <div className="mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {editingScholarship ? 'Təqaüdü Redaktə Et' : 'Yeni Təqaüd Əlavə Et'}
                    </h3>
                    <p className="mt-1 text-sm text-gray-600">
                      {editingScholarship ? 'Təqaüd məlumatlarını yeniləyin' : 'Yeni təqaüd məlumatlarını daxil edin'}
                    </p>
                  </div>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <span className="text-2xl">&times;</span>
                  </button>
                </div>
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Information Section */}
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
                  <h4 className="text-lg font-semibold text-blue-900 mb-4">Əsas Məlumatlar</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Təqaüd Adı *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Məsələn: DAAD Scholarship Program"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Universitet *
                      </label>
                      <Select
                        value={universitiesData?.data?.data?.find(uni => uni.id.toString() === formData.university_id) ? {
                          value: formData.university_id,
                          label: universitiesData?.data?.data?.find(uni => uni.id.toString() === formData.university_id)?.name
                        } : null}
                        onChange={(selectedOption) => {
                          const selectedUni = universitiesData?.data?.data?.find(uni => uni.id === parseInt(selectedOption?.value || ''));
                          setFormData({ 
                            ...formData, 
                            university_id: selectedOption?.value || '',
                            country_id: selectedUni ? selectedUni.country_id?.toString() || '' : '',
                            city_id: selectedUni ? selectedUni.city_id?.toString() || '' : ''
                          });
                        }}
                        options={universitiesData?.data?.data?.map(university => ({
                          value: university.id.toString(),
                          label: `${university.name} (${university.country?.name || ''})`
                        })) || []}
                        placeholder="Universitet seçin və ya axtarın..."
                        isSearchable={true}
                        isClearable={true}
                        styles={{
                          control: (provided, state) => ({
                            ...provided,
                            borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                            boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
                            '&:hover': {
                              borderColor: state.isFocused ? '#3b82f6' : '#9ca3af'
                            }
                          }),
                          option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.isSelected 
                              ? '#3b82f6' 
                              : state.isFocused 
                              ? '#dbeafe' 
                              : 'white',
                            color: state.isSelected ? 'white' : '#374151'
                          })
                        }}
                        className="react-select-container"
                        classNamePrefix="react-select"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ölkə
                      </label>
                      <Select
                        value={countriesData?.data?.data?.find(country => country.id.toString() === formData.country_id) ? {
                          value: formData.country_id,
                          label: countriesData?.data?.data?.find(country => country.id.toString() === formData.country_id)?.name
                        } : null}
                        onChange={(selectedOption) => {
                          setFormData({ 
                            ...formData, 
                            country_id: selectedOption?.value || '',
                            city_id: '' // Reset city when country changes
                          });
                        }}
                        options={countriesData?.data?.data?.map(country => ({
                          value: country.id.toString(),
                          label: country.name
                        })) || []}
                        placeholder="Ölkə seçin..."
                        isSearchable={true}
                        isClearable={true}
                        styles={{
                          control: (provided, state) => ({
                            ...provided,
                            borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                            boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
                            '&:hover': {
                              borderColor: state.isFocused ? '#3b82f6' : '#9ca3af'
                            }
                          }),
                          option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.isSelected 
                              ? '#3b82f6' 
                              : state.isFocused 
                              ? '#dbeafe' 
                              : 'white',
                            color: state.isSelected ? 'white' : '#374151'
                          })
                        }}
                        className="react-select-container"
                        classNamePrefix="react-select"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Şəhər
                      </label>
                      <Select
                        value={citiesData?.data?.data?.find(city => city.id.toString() === formData.city_id) ? {
                          value: formData.city_id,
                          label: citiesData?.data?.data?.find(city => city.id.toString() === formData.city_id)?.name
                        } : null}
                        onChange={(selectedOption) => {
                          setFormData({ 
                            ...formData, 
                            city_id: selectedOption?.value || ''
                          });
                        }}
                        options={formData.country_id ? 
                          citiesData?.data?.data?.filter(city => city.country_id.toString() === formData.country_id)?.map(city => ({
                            value: city.id.toString(),
                            label: city.name
                          })) || [] : []
                        }
                        placeholder={formData.country_id ? "Şəhər seçin..." : "Əvvəl ölkə seçin"}
                        isSearchable={true}
                        isClearable={true}
                        isDisabled={!formData.country_id}
                        styles={{
                          control: (provided, state) => ({
                            ...provided,
                            borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                            boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
                            '&:hover': {
                              borderColor: state.isFocused ? '#3b82f6' : '#9ca3af'
                            }
                          }),
                          option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.isSelected 
                              ? '#3b82f6' 
                              : state.isFocused 
                              ? '#dbeafe' 
                              : 'white',
                            color: state.isSelected ? 'white' : '#374151'
                          })
                        }}
                        className="react-select-container"
                        classNamePrefix="react-select"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Dərəcə Səviyyəsi *
                      </label>
                      <Select
                        value={educationLevels && formData.degree_level && Array.isArray(formData.degree_level) ? 
                          formData.degree_level.map(levelId => {
                            const level = educationLevels.find(l => l.id && l.id.toString() === levelId.toString());
                            return level ? { value: level.id.toString(), label: level.name } : null;
                          }).filter(Boolean) : []
                        }
                        onChange={(selectedOptions) => {
                          const selectedIds = selectedOptions && Array.isArray(selectedOptions) ? 
                            selectedOptions.map(option => option.value) : [];
                          setFormData({ ...formData, degree_level: selectedIds });
                        }}
                        options={educationLevels && Array.isArray(educationLevels) ? 
                          educationLevels.map(level => ({
                            value: level.id ? level.id.toString() : '',
                            label: level.name || ''
                          })) : []}
                        placeholder="Dərəcə səviyyələri seçin..."
                        isSearchable={true}
                        isMulti={true}
                        isClearable={true}
                        styles={{
                          control: (provided, state) => ({
                            ...provided,
                            borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                            boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
                            '&:hover': {
                              borderColor: state.isFocused ? '#3b82f6' : '#9ca3af'
                            }
                          }),
                          option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.isSelected 
                              ? '#3b82f6' 
                              : state.isFocused 
                              ? '#dbeafe' 
                              : 'white',
                            color: state.isSelected ? 'white' : '#374151'
                          })
                        }}
                        className="react-select-container"
                        classNamePrefix="react-select"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sahə *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.field_of_study}
                        onChange={(e) => setFormData({ ...formData, field_of_study: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Məsələn: Mühendislik, İT, Biznes"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Təqaüd Təsviri *
                      </label>
                      <textarea
                        rows={3}
                        required
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Təqaüd proqramı haqqında qısa məlumat..."
                      />
                    </div>
                  </div>
                </div>

                {/* Financial Information Section */}
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
                  <h4 className="text-lg font-semibold text-blue-900 mb-4">Maliyyə Məlumatları</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Məbləğ *
                      </label>
                      <input
                        type="number"
                        required
                        value={formData.amount}
                        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="25000"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Valyuta *
                      </label>
                      <Select
                        value={currencies && formData.currency_id ? 
                          (() => {
                            const foundCurrency = currencies.find(curr => curr.id && curr.id.toString() === formData.currency_id.toString());
                            return foundCurrency ? 
                              { value: foundCurrency.id.toString(), label: `${foundCurrency.code} - ${foundCurrency.name}` } : 
                              null;
                          })()
                          : null}
                        onChange={(selectedOption) => {
                          setFormData({ ...formData, currency_id: selectedOption?.value || '' });
                        }}
                        options={currencies ? currencies.map(currency => ({
                          value: currency.id.toString(),
                          label: `${currency.code} - ${currency.name}`
                        })) : []}
                        placeholder="Valyuta seçin və ya axtarın..."
                        isSearchable={true}
                        isClearable={true}
                        styles={{
                          control: (provided, state) => ({
                            ...provided,
                            borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                            boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
                            '&:hover': {
                              borderColor: state.isFocused ? '#3b82f6' : '#9ca3af'
                            }
                          }),
                          option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.isSelected 
                              ? '#3b82f6' 
                              : state.isFocused 
                              ? '#dbeafe' 
                              : 'white',
                            color: state.isSelected ? 'white' : '#374151',
                            '&:hover': {
                              backgroundColor: state.isSelected ? '#3b82f6' : '#dbeafe'
                            }
                          })
                        }}
                        className="react-select-container"
                        classNamePrefix="react-select"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ödəniş Növü *
                      </label>
                      <Select
                        value={formData.amount_type ? {
                          value: formData.amount_type,
                          label: formData.amount_type
                        } : null}
                        onChange={(selectedOption) => {
                          setFormData({ ...formData, amount_type: selectedOption?.value || '' });
                        }}
                        options={[
                          { value: 'Aylıq', label: 'Aylıq' },
                          { value: 'İllik', label: 'İllik' },
                          { value: 'Birdəfəlik', label: 'Birdəfəlik' }
                        ]}
                        placeholder="Ödəniş növü seçin..."
                        isSearchable={true}
                        isClearable={true}
                        styles={{
                          control: (provided, state) => ({
                            ...provided,
                            borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                            boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
                            '&:hover': {
                              borderColor: state.isFocused ? '#3b82f6' : '#9ca3af'
                            }
                          }),
                          option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.isSelected 
                              ? '#3b82f6' 
                              : state.isFocused 
                              ? '#dbeafe' 
                              : 'white',
                            color: state.isSelected ? 'white' : '#374151'
                          })
                        }}
                        className="react-select-container"
                        classNamePrefix="react-select"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Əhatə Növü *
                      </label>
                      <Select
                        value={formData.coverage_type ? {
                          value: formData.coverage_type,
                          label: formData.coverage_type
                        } : null}
                        onChange={(selectedOption) => {
                          setFormData({ ...formData, coverage_type: selectedOption?.value || '' });
                        }}
                        options={[
                          { value: 'Tam təqaüd', label: 'Tam təqaüd' },
                          { value: 'Qismən təqaüd', label: 'Qismən təqaüd' }
                        ]}
                        placeholder="Əhatə növü seçin..."
                        isSearchable={true}
                        isClearable={true}
                        styles={{
                          control: (provided, state) => ({
                            ...provided,
                            borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                            boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
                            '&:hover': {
                              borderColor: state.isFocused ? '#3b82f6' : '#9ca3af'
                            }
                          }),
                          option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.isSelected 
                              ? '#3b82f6' 
                              : state.isFocused 
                              ? '#dbeafe' 
                              : 'white',
                            color: state.isSelected ? 'white' : '#374151'
                          })
                        }}
                        className="react-select-container"
                        classNamePrefix="react-select"
                      />
                    </div>
                    
                    <div className="lg:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Müddət *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.duration}
                        onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Məsələn: 12 ay, 24 ay"
                      />
                    </div>
                    
                    <div className="lg:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Əlavə Faydalar
                      </label>
                      <input
                        type="text"
                        value={formData.additional_benefits}
                        onChange={(e) => setFormData({ ...formData, additional_benefits: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Yaşayış, səyahət, sığorta və s."
                      />
                    </div>
                  </div>
                </div>

                {/* Application Information Section */}
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
                  <h4 className="text-lg font-semibold text-blue-900 mb-4">Müraciət Məlumatları</h4>
                  <div className="grid grid-cols-1 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Son Müraciət Tarixi *
                      </label>
                      <input
                        type="date"
                        required
                        value={formData.application_deadline}
                        onChange={(e) => setFormData({ ...formData, application_deadline: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Tələb Olunan Sənədlər *
                      </label>
                      <textarea
                        rows={3}
                        required
                        value={formData.requirements}
                        onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="IELTS, GPA, CV, Motivasiya məktubu və s."
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Müraciət Prosesi *
                      </label>
                      <textarea
                        rows={2}
                        required
                        value={formData.application_process}
                        onChange={(e) => setFormData({ ...formData, application_process: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Online müraciət prosesi haqqında məlumat..."
                      />
                    </div>
                  </div>
                </div>

                {/* Contact Information Section */}
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
                  <h4 className="text-lg font-semibold text-blue-900 mb-4">Əlaqə Məlumatları</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        required
                        value={formData.contact_email}
                        onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="contact@scholarship.org"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Vebsayt *
                      </label>
                      <input
                        type="url"
                        required
                        value={formData.website}
                        onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="https://www.scholarship.org"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <div className="flex items-center space-x-3">
                        <input
                          type="checkbox"
                          checked={formData.is_active}
                          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">
                          Təqaüd proqramı aktiv statusdadır
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Ləğv et
                  </button>
                  {(editingScholarship ? canEditScholarship : canAddScholarship) && (
                    <button
                      type="submit"
                      className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      {editingScholarship ? 'Yenilə' : 'Yadda saxla'}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 